'use client';

import Script from 'next/script';
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { plausibleConfig } from './config';
import { bootstrap, syncOptOut, track } from './browser';

const pendingSignup = new Set<string>();
let signupRequest: Promise<string | undefined> | undefined;
function clearSignupCookies() {
  document.cookie = 'plausible_signup=; Path=/; Max-Age=0; SameSite=Lax';
  document.cookie = 'plausible_signup_start=; Path=/; Max-Age=0; SameSite=Lax';
}
async function signupToken() {
  if (!signupRequest) {
    signupRequest = fetch('/api/analytics/signup', {
      method: 'POST',
      cache: 'no-store',
      signal: AbortSignal.timeout(4000),
    })
      .then(async (response) =>
        response.ok
          ? ((await response.json()).token as string | undefined)
          : undefined
      )
      .catch(() => undefined)
      .finally(() => {
        signupRequest = undefined;
      });
  }
  return signupRequest;
}

export default function PlausibleAnalytics() {
  const pathname = usePathname();
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' || syncOptOut()) return;
    let cancelled = false;
    const marker = document.cookie.match(
      /(?:^|;\s*)plausible_signup=([^;]+)/
    )?.[1];
    const started = document.cookie.includes('plausible_signup_start=');
    async function signup() {
      let token = marker;
      if (!token && started) {
        try {
          token = await signupToken();
        } catch {
          return;
        }
      }
      if (!token || cancelled) return;
      const key = `plausible:Signup:${token}`;
      try {
        if (localStorage.getItem(key)) {
          clearSignupCookies();
          return;
        }
      } catch {}
      if (pendingSignup.has(key)) return;
      pendingSignup.add(key);
      const retryTimer = setTimeout(() => pendingSignup.delete(key), 10_000);
      track('Signup', undefined, (result) => {
        clearTimeout(retryTimer);
        pendingSignup.delete(key);
        if (result?.error || (result?.status && result.status >= 400)) return;
        try {
          localStorage.setItem(key, '1');
        } catch {}
        clearSignupCookies();
      });
    }
    void signup();
    return () => {
      cancelled = true;
    };
  }, [pathname]);
  if (process.env.NODE_ENV !== 'production') return null;
  return (
    <>
      <Script id="plausible-init" strategy="beforeInteractive">
        {bootstrap}
      </Script>
      <Script async src={plausibleConfig.script} strategy="afterInteractive" />
    </>
  );
}
